import Vue from 'vue'
import { i18n } from '@/plugins/i18n'
import { v2commissionService, scraperService } from '@/services/api'
import { groupByArrayOfObject, mergeIt } from '@/common/arrays'
import { filterObject } from '@/common/objects'
import { CommissionModel } from '@/constants'

const initialStates = {
    status: 'LOADING',
    commissionsList: {},
    pagination: {
        no_paginate: false,
        per_page: 20,
        current_page: 1,
        last_page: 1,
    },
    params: {
        page: 1
    }
}

const mutations = {
    loading(state) {
        state.status = 'LOADING'
    },
    setCommissionsList(state, commissions) {
        state.status       = 'SET_LIST'
        state.commissionsList = commissions
    },
    setPagination(state, pagination) {
        state.status     = 'SET_PAGINATION'
        state.pagination = Object.assign({}, state.pagination, pagination)
    },
    setParams(state, params) {
        state.status = 'SET_PARAMS'
        state.params = Object.assign({}, state.params, params)
    },
    resetParams(state) {
        state.status = 'RESET_PARAMS'
        state.params = {
            page: 1
        }
    },
    createCommission(state, commission) {
        state.status = 'CREATE'
        state.commissionsList = Object.assign({[commission.id]: commission}, state.commissionsList)
    },
    updateCommission(state, commission) {
        state.status = 'UPDATE'
        state.commissionsList[commission.id] = Object.assign({}, state.commissionsList[commission.id], commission)
    },
    deleteCommission(state, id) {
        state.status = 'DELETE_FROM_LIST'
        Vue.delete(state.commissionsList, id)
        // delete state.commissionsList[id]
    },
    error(state) {
        state.status = 'ERROR'
    },
}

const getters = {
    status: state => state.status,
    commissions: state => state.commissionsList,
    commissionById: state => id => state.commissionsList[id],
    defaults: state => filterObject(
        state.commissionsList,
        commission => commission.model === CommissionModel.DEFAULT
    ),
    groups: state => filterObject(
        state.commissionsList,
        commission => commission.model === CommissionModel.GROUP
    ),
    commissionRules: state =>  filterObject(
        state.commissionsList, commission => [CommissionModel.INDIVIDUAL_COMMISSION_RULE, CommissionModel.GLOBAL_COMMISSION_RULE].includes(commission.model)
    ),
    globalCommissionRules: state => filterObject(
        state.commissionsList, commission => commission.model === CommissionModel.GLOBAL_COMMISSION_RULE
    ),
    individualCommissionRules: state => filterObject(
        state.commissionsList, commission => commission.model === CommissionModel.INDIVIDUAL_COMMISSION_RULE
    ),
    individualCommissionRulesWherePublisherId: state => publisherId => filterObject(
        state.commissionsList,
        commission => commission.model === CommissionModel.INDIVIDUAL_COMMISSION_RULE && commission.publisher_id === publisherId
    ),
    categoryBaseCommissionWhereStoreId: state => storeId => filterObject(
        state.commissionsList,
        commission => commission.model === CommissionModel.CATEGORY_BASE && commission.store_id === storeId
    ),
    productBaseCommissionWhereStoreId: state => storeId => filterObject(
        state.commissionsList,
        commission => commission.model === CommissionModel.PRODUCT_BASE && commission.store_id === storeId
    ),
    exclusiveCommissionWherePublisherId: state => publisherId => filterObject(
        state.commissionsList,
        commission => commission.model === CommissionModel.INDIVIDUAL && commission.publisher_id === publisherId
    ),
    tiers: state => filterObject(
        state.commissionsList,
        commission => commission.model === CommissionModel.TRANSACTION_TIER
    ),
    categoryBaseCommissions: state => filterObject(
        state.commissionsList,
        commission => commission.model === CommissionModel.CATEGORY_BASE
    ),
    productBaseCommissions: state => filterObject(
        state.commissionsList,
        commission => commission.model === CommissionModel.PRODUCT_BASE
    ),
    selectableCommissionsGroup: (state, getters) => {
        return Object.keys(getters.groups).map((id) => {
            return {
                value: id,
                label: getters.groups[id].name,
            }
        })
    },
    selectableCommissionsGroupWithNull: (state, getters) => {
        return [
            {
                label: i18n.t('select.label_placeholder', {attribute: i18n.t('v2commission.group._')}),
                value: ''
            },
            ...getters.selectableCommissionsGroup
        ]
    },
    // arrayRules: (state) => Object.entries(filterObject(
    //     state.commissionsList, commission => commission.model === CommissionModel.GLOBAL_COMMISSION_RULE
    // )).map((e) => ( e[1] ))
}

const actions = {
    setParamsAndFetch({dispatch}, params) {
        dispatch('setParams', params)
        dispatch('fetch')
    },
    setParams({commit}, params) {
        commit('setParams', params)
    },
    resetParams({commit}) {
        commit('resetParams')
    },
    fetch({commit, state, rootGetters}, params) {
        params = Object.assign({}, state.params, params)

        commit('loading')

        return new Promise((resolve, reject) => {
            v2commissionService.fetch(params)
                .then(response => {
                    const result = response.data
                    const commissions = groupByArrayOfObject(result.data.sort(), 'id')

                    commit('setCommissionsList', commissions)
                    if (params.no_paginate) {
                        commit('setPagination', {
                            no_paginate: true
                        })
                    } else {
                        commit('setPagination', {
                            per_page: result.per_page,
                            current_page: result.current_page,
                            last_page: result.last_page,
                            no_paginate: false,
                        })
                        commit('setParams', {
                            page: result.current_page,
                        })
                    }

                    resolve(result)


                    // // We must get all site categories and attach them to the product_groups
                    // const promises = result.data.filter(item => item.model === 5).map((catCommission) => {
                    //     if (typeof catCommission.product_groups === 'object') {
                    //         catCommission.product_groups = Object.values(catCommission.product_groups)
                    //     }
                    //     const ids = catCommission.product_groups.map(item => item.id)

                    //     return scraperService.fetchCategory({
                    //         site_id: rootGetters['scraperSite/siteId'],
                    //         unlimited: true,
                    //         _id: ids
                    //     })
                    //     .then(response => {
                    //         const res = response.data.data
                    //         commissions[catCommission.id].product_groups = commissions[catCommission.id].product_groups.map((pr) => {
                    //             return {
                    //                 ...pr,
                    //                 title: res.find(el => el._id === pr.id).title || ''
                    //             }
                    //         })
                    //     })
                    //     .catch(error => {
                    //         commit('error')
                    //         reject(error.response.data || error.response || error)
                    //     })
                    // })

                    // Promise.all(promises).finally(() => {
                    //     commit('setCommissionsList', commissions)
                    //     if (params.no_paginate) {
                    //         commit('setPagination', {
                    //             no_paginate: true
                    //         })
                    //     } else {
                    //         commit('setPagination', {
                    //             per_page: result.per_page,
                    //             current_page: result.current_page,
                    //             last_page: result.last_page,
                    //             no_paginate: false,
                    //         })
                    //         commit('setParams', {
                    //             page: result.current_page,
                    //         })
                    //     }

                    //     resolve(result)
                    // })
                })
                .catch(error => {
                    commit('error')
                    reject(error.response.data || error.response || error)
                })
        })
    },
    create({commit}, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            v2commissionService.create(params)
                .then(response => {
                    const result = response.data
                    commit('createCommission', result.data)
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    edit({commit}, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            v2commissionService.edit(params, params.id)
                .then(() => {
                    commit('updateCommission', params)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    updateCommissionRule({commit, getters}, params) {
        const commissionRules = getters.commissionRules

        const commissionRuleId = Object.keys(commissionRules).find(id => id === params.id)
        if (commissionRuleId) {
            commissionRules[commissionRuleId].rules = params.rules
            commit('updateCommission', commissionRules[commissionRuleId])
        }
    },
    addConditionToRule({commit, getters}, params) {
        const commissionRules = getters.commissionRules

        Object.keys(commissionRules).map(id => {
            const ruleIndex = Object.keys(commissionRules[id].rules).find(index => commissionRules[id].rules[index].id === params.rule_id)
            if (ruleIndex) {
                commissionRules[id].rules[ruleIndex]['conditions'] = mergeIt(
                    commissionRules[id].rules[ruleIndex]['conditions'] || [], [params.condition]
                )
                commit('updateCommission', commissionRules[id])
            }
        })
    },
    delete({commit}, id) {
        commit('loading')

        return new Promise((resolve, reject) => {
            v2commissionService.delete(id)
                .then(() => {
                    commit('deleteCommission', id)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
}

export default {
    namespaced: true,
    state: initialStates,
    mutations: mutations,
    getters: getters,
    actions: actions,
}